import { formatDate } from '@/utils';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router';
import {
  useGetMonthlyHolidaysQuery,
  useGetMonthlyLeavesQuery,
} from '../../../../../../app/features/employee/emp-leave/emp-leave.api';
import useAuth from '../../../../../../hooks/useAuth';
import useModal from '../../../../../../hooks/useModal';
import { cn } from '../../../../../../lib/cn';
import { holidayTypes } from '../../../_data/data';
import EmployeeLeaveView from './EmployeeLeaveView';

// COLOR STYLES FOR INDICATORS
const INDICATOR_COLORS = {
  government: '#3E8BFF', // Blue
  festival: '#00C897', // Green
  events: '#C026D3', // Purple
  holiday: '#6B7280', // Gray
  leave: '#FACC15', // Yellow
};

export default function LeaveSummery() {
  const { organizationId } = useAuth();
  const { open, handleOpen, handleClose } = useModal<
    {
      date: string;
      leaves: any[];
      holidays?: any[];
      day: number;
      month: number;
      year: number;
    },
    'VIEW'
  >();
  const [searchParams] = useSearchParams();
  const dateParam = searchParams.get('date');
  const dept = searchParams.get('department');
  const search = searchParams.get('search');

  const { currentMonth, currentYear } = useMemo(() => {
    if (dateParam) {
      const date = dayjs(dateParam);
      return {
        currentMonth: date.month() + 1,
        currentYear: date.year(),
      };
    }

    const now = dayjs();
    return {
      currentMonth: now.month() + 1,
      currentYear: now.year(),
    };
  }, [dateParam]);

  const {
    data: holidaysData,
    isLoading: holidaysLoading,
    isFetching: holidaysFetching,
  } = useGetMonthlyHolidaysQuery({
    params: {
      organizationId,
      month: currentMonth,
      year: currentYear,
    },
  });

  const {
    data: leavesData,
    isLoading: leavesLoading,
    isFetching: leavesFetching,
  } = useGetMonthlyLeavesQuery({
    params: {
      organization_id: organizationId,
      start_date: `${currentYear}-${String(currentMonth).padStart(2, '0')}-01`,
      end_date: dayjs(`${currentYear}-${currentMonth}-01`)
        .endOf('month')
        .format('YYYY-MM-DD'),
      dept: dept ? String(dept) : undefined,
      search: search ? String(search) : undefined,
      status: 'APPROVED',
    },
  });

  const { eventMap, leaveDataMap, holidayDataMap } = useMemo<{
    eventMap: Record<
      number,
      { type: keyof typeof INDICATOR_COLORS; label: string }[]
    >;
    leaveDataMap: Record<number, any[]>;
    holidayDataMap: Record<number, any[]>;
  }>(() => {
    const map: Record<
      number,
      { type: keyof typeof INDICATOR_COLORS; label: string }[]
    > = {};
    const leaveMap: Record<number, any[]> = {};
    const holidayMap: Record<number, any[]> = {};

    // Map holidays
    if (holidaysData?.data?.data) {
      holidaysData.data.data.forEach((holiday: any) => {
        const holidayDate = dayjs(holiday?.date);
        const dayOfMonth = holidayDate.date();

        if (!map[dayOfMonth]) map[dayOfMonth] = [];
        if (!holidayMap[dayOfMonth]) holidayMap[dayOfMonth] = [];

        let colorType: keyof typeof INDICATOR_COLORS;
        switch (holiday.type) {
          case 'HOLIDAY':
            colorType = 'holiday';
            break;
          case 'FESTIVAL':
            colorType = 'festival';
            break;
          case 'EVENTS':
            colorType = 'events';
            break;
          case 'GOVERNMENT':
            colorType = 'government';
            break;
          default:
            colorType = 'government';
        }

        map[dayOfMonth].push({ type: colorType, label: holiday.name });
        holidayMap[dayOfMonth].push(holiday);
      });
    }

    // Map leaves
    if (leavesData?.data?.data) {
      leavesData.data.data.forEach((leave: any) => {
        const leaveStart = dayjs(leave.start_date);
        const leaveEnd = dayjs(leave.end_date);
        let currentDate = leaveStart;
        const monthEnd = dayjs(`${currentYear}-${currentMonth}`).endOf('month');
        const endDate = leaveEnd.isAfter(monthEnd) ? monthEnd : leaveEnd;

        while (
          currentDate.isBefore(endDate) ||
          currentDate.isSame(endDate, 'day')
        ) {
          const dayOfMonth = currentDate.date();
          if (!leaveMap[dayOfMonth]) leaveMap[dayOfMonth] = [];
          leaveMap[dayOfMonth].push(leave);
          currentDate = currentDate.add(1, 'day');
        }
      });
    }

    return {
      eventMap: map,
      leaveDataMap: leaveMap,
      holidayDataMap: holidayMap,
    };
  }, [holidaysData, leavesData, currentMonth, currentYear]);

  const calendarDaysWithContext = useMemo(() => {
    const firstDay = dayjs(`${currentYear}-${currentMonth}-01`);
    const lastDay = firstDay.endOf('month');

    const lastDate = lastDay.date();
    const firstDayOfWeek = (firstDay.day() + 6) % 7;

    const result: Array<{
      day: number;
      isCurrentMonth: boolean;
      weekIndex: number;
      dayIndex: number;
    }> = [];

    let weekIndex = 0;
    const prevMonthLastDate = firstDay.subtract(1, 'day').date();

    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      result.push({
        day: prevMonthLastDate - i,
        isCurrentMonth: false,
        weekIndex,
        dayIndex: firstDayOfWeek - 1 - i,
      });
    }

    for (let day = 1; day <= lastDate; day++) {
      const dayIndex = (firstDayOfWeek + day - 1) % 7;

      if (dayIndex === 0 && day > 1) weekIndex++;

      result.push({
        day,
        isCurrentMonth: true,
        weekIndex,
        dayIndex,
      });
    }

    let nextMonthDay = 1;
    while (result.length < 42) {
      const dayIndex = result.length % 7;

      if (dayIndex === 0) weekIndex++;

      result.push({
        day: nextMonthDay++,
        isCurrentMonth: false,
        weekIndex,
        dayIndex,
      });
    }

    return result;
  }, [currentMonth, currentYear]);

  const DayCell = ({
    day,
    isCurrentMonth,
    events,
    leaveData,
  }: {
    day: number;
    isCurrentMonth: boolean;
    events?: Array<{ type: keyof typeof INDICATOR_COLORS; label: string }>;
    leaveData?: any[];
  }) => {
    const textOpacity = isCurrentMonth ? 'text-white' : 'text-gray-500';

    const handleCellClick = () => {
      if (!isCurrentMonth) return;

      const holidayDataForDay = holidayDataMap?.[day] ?? [];
      const hasLeave = leaveData && leaveData.length > 0;
      const hasHoliday = holidayDataForDay.length > 0;

      if (!hasLeave && !hasHoliday) return;

      handleOpen({
        type: 'VIEW',
        data: {
          date: `${currentYear}-${String(currentMonth).padStart(
            2,
            '0',
          )}-${String(day).padStart(2, '0')}`,
          leaves: hasLeave ? leaveData : [],
          holidays: hasHoliday ? holidayDataForDay : [],
          day,
          month: currentMonth,
          year: currentYear,
        },
      });
    };
    return (
      <div
        className={cn(
          '!w-36 !h-40 rounded-xl bg-transparent border-2 border-slate-700 flex flex-col p-2 text-center relative',
          textOpacity,
          isCurrentMonth &&
            ((leaveData && leaveData.length > 0) ||
              holidayDataMap?.[day]?.length > 0) &&
            'cursor-pointer hover:bg-slate-700/20',
        )}
        onClick={handleCellClick}
      >
        <span className="text-lg font-semibold mb-2">{day}</span>

        {isCurrentMonth && events && events.length > 0 && (
          <div className="flex flex-col gap-1 text-left text-sm text-white overflow-hidden">
            {events.map((event, idx) => (
              <div key={idx} className="flex items-start gap-1.5">
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0 mt-0.5"
                  style={{ backgroundColor: INDICATOR_COLORS[event.type] }}
                ></span>
                <span className="leading-tight line-clamp-2">
                  {event.label}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="w-full dark:bg-theme-slate-800 border dark:border-theme-gray-700 rounded-xl text-white p-6 custom-scroll overflow-x-auto">
        <div className="flex items-start justify-between bg-transparent h-[52px] rounded-md !mb-12">
          <div>
            <div className="text-xl lg:text-3xl font-bold">
              Organization Calendar
            </div>
            <div className="justify-start text-white text-lg font-normal capitalize mt-1">
              {formatDate(
                dayjs()
                  .year(currentYear)
                  .month(currentMonth - 1)
                  .toString(),
                'MMMM YYYY',
              )}
            </div>
          </div>

          <div className="flex flex-col text-white text-base font-normal">
            <span className="mb-4 text-xl font-semibold text-white">
              Indicator
            </span>

            <div className="flex items-center gap-6">
              {holidayTypes?.map((el) => (
                <span className="flex items-center gap-2" key={el.value}>
                  <span
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: el.color }}
                  />
                  {el.label}
                </span>
              ))}

              {/* <span className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-[#00C897]"></span>
                Festival
              </span>

              <span className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-[#C026D3]"></span>
                Event
              </span>

              <span className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-[#6B7280]"></span>
                Weekly Holiday
              </span> */}

              <span className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-[#FACC15]"></span>
                Employee Leave
              </span>
            </div>
          </div>
        </div>

        {holidaysLoading ||
        holidaysFetching ||
        leavesLoading ||
        leavesFetching ? (
          <div className="grid grid-cols-7 gap-x-7 gap-y-3">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
              <div
                key={`header-${d}`}
                className="!w-36 !h-36 rounded-xl bg-[#374151] border border-slate-700 flex items-center justify-center text-center animate-pulse"
              >
                <div className=" bg-slate-600 rounded"></div>
              </div>
            ))}

            {Array.from({ length: 42 }, (_, index) => (
              <div
                key={`skeleton-${index}`}
                className="!w-36 !h-36 rounded-xl bg-transparent border border-slate-700 flex flex-col p-2 animate-pulse"
              >
                <div className="flex justify-center mb-2">
                  <div className="w-8 h-8 bg-slate-600 rounded-full"></div>
                </div>
                <div className="flex-1 space-y-1">
                  <div className="w-full h-3 bg-slate-600 rounded"></div>
                  <div className="w-3/4 h-3 bg-slate-600 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-7 gap-x-7 gap-y-3">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
              <div
                key={d}
                className="!w-36 !h-36 rounded-xl flex items-center justify-center text-center bg-[#374151] border border-slate-700 text- text-xl font-bold"
              >
                {d}
              </div>
            ))}

            {calendarDaysWithContext.map((dayInfo, index) => {
              const { day, isCurrentMonth, dayIndex } = dayInfo;

              const isFriday = dayIndex === 4;
              const dayLeaveData = isCurrentMonth
                ? leaveDataMap[day]
                : undefined;

              let events: Array<{
                type: keyof typeof INDICATOR_COLORS;
                label: string;
              }> = [];

              // Add weekly holiday for Friday
              if (isCurrentMonth && isFriday) {
                events.push({
                  type: 'holiday',
                  label: 'Weekly Holiday',
                });
              }

              // Add holidays from API
              if (isCurrentMonth && eventMap[day]) {
                events = [...events, ...eventMap[day]];
              }

              // Add leave indicator
              if (dayLeaveData && dayLeaveData.length > 0) {
                const leaveCount = dayLeaveData.length;
                events.push({
                  type: 'leave',
                  label: `${leaveCount} Employee${
                    leaveCount > 1 ? 's' : ''
                  } On Leave`,
                });
              }

              return (
                <DayCell
                  key={index}
                  day={day}
                  isCurrentMonth={isCurrentMonth}
                  events={events.length > 0 ? events : undefined}
                  leaveData={dayLeaveData}
                />
              );
            })}
          </div>
        )}
      </div>

      <EmployeeLeaveView
        data={open.data}
        type={open.type}
        handleClose={handleClose}
        open={
          open.open &&
          open.type === 'VIEW' &&
          (!!open.data?.leaves?.length || !!open.data?.holidays?.length)
        }
      />
    </>
  );
}

/* 
// TODO: eta width full rakha
import dayjs from "dayjs";
import { useMemo } from "react";
import { useSearchParams } from "react-router";
import {
  useGetMonthlyHolidaysQuery,
  useGetMonthlyLeavesQuery,
} from "../../../../../../app/features/employee/emp-leave/emp-leave.api";
import useAuth from "../../../../../../hooks/useAuth";
import useModal from "../../../../../../hooks/useModal";
import { cn } from "../../../../../../lib/cn";
// import { formatDate } from '@/utils';
import EmployeeLeaveView from "./EmployeeLeaveView";

// COLOR STYLES FOR INDICATORS
const INDICATOR_COLORS = {
  government: "#3E8BFF", // Blue
  festival: "#00C897", // Green
  events: "#C026D3", // Purple
  holiday: "#6B7280", // Gray
  leave: "#FACC15", // Yellow
};

export default function LeaveSummery() {
  const { organizationId } = useAuth();
  const { open, handleOpen, handleClose } = useModal<
    {
      date: string;
      leaves: any[];
      day: number;
      month: number;
      year: number;
    },
    "VIEW"
  >();
  const [searchParams] = useSearchParams();
  const dateParam = searchParams.get("date");
  const dept = searchParams.get("department");
  const search = searchParams.get("search");

  const { currentMonth, currentYear } = useMemo(() => {
    if (dateParam) {
      const date = dayjs(dateParam);
      return {
        currentMonth: date.month() + 1,
        currentYear: date.year(),
      };
    }

    const now = dayjs();
    return {
      currentMonth: now.month() + 1,
      currentYear: now.year(),
    };
  }, [dateParam]);

  const {
    data: holidaysData,
    isLoading: holidaysLoading,
    isFetching: holidaysFetching,
  } = useGetMonthlyHolidaysQuery({
    params: {
      organizationId,
      month: currentMonth,
      year: currentYear,
    },
  });

  const {
    data: leavesData,
    isLoading: leavesLoading,
    isFetching: leavesFetching,
  } = useGetMonthlyLeavesQuery({
    params: {
      organization_id: organizationId,
      start_date: `${currentYear}-${String(currentMonth).padStart(2, "0")}-01`,
      end_date: dayjs(`${currentYear}-${currentMonth}-01`)
        .endOf("month")
        .format("YYYY-MM-DD"),
      dept: dept ? String(dept) : undefined,
      search: search ? String(search) : undefined,
      status: "APPROVED",
    },
  });

  const { eventMap, leaveDataMap } = useMemo(() => {
    const map: Record<number, Array<{ type: keyof typeof INDICATOR_COLORS; label: string }>> = {};
    const leaveMap: Record<number, any[]> = {};

    // Add holidays from API
    if (holidaysData?.data?.data) {
      holidaysData.data.data.forEach((holiday: any) => {
        const holidayDate = dayjs(holiday?.date);
        const holidayMonth = holidayDate.month() + 1;
        const holidayYear = holidayDate.year();
        const dayOfMonth = holidayDate.date();

        if (holidayMonth === currentMonth && holidayYear === currentYear) {
          if (!map[dayOfMonth]) {
            map[dayOfMonth] = [];
          }

          let colorType: keyof typeof INDICATOR_COLORS;
          switch (holiday.type) {
            case "HOLIDAY":
              colorType = "holiday";
              break;
            case "FESTIVAL":
              colorType = "festival";
              break;
            case "EVENTS":
              colorType = "events";
              break;
            case "GOVERNMENT":
              colorType = "government";
              break;
            default:
              colorType = "government";
          }

          map[dayOfMonth].push({
            type: colorType,
            label: holiday.name,
          });
        }
      });
    }

    // Add leaves from API
    if (leavesData?.data?.data) {
      const leavesByDay: Record<number, any[]> = {};

      leavesData.data.data.forEach((leave: any) => {
        const leaveStartDate = dayjs(leave.start_date);
        const leaveEndDate = dayjs(leave.end_date);
        const leaveMonth = leaveStartDate.month() + 1;
        const leaveYear = leaveStartDate.year();

        if (leaveMonth === currentMonth && leaveYear === currentYear) {
          let currentDate = leaveStartDate;
          const endDate = leaveEndDate.isAfter(
            dayjs(`${currentYear}-${currentMonth}`).endOf("month")
          )
            ? dayjs(`${currentYear}-${currentMonth}`).endOf("month")
            : leaveEndDate;

          while (
            currentDate.isBefore(endDate) ||
            currentDate.isSame(endDate, "day")
          ) {
            const dayOfMonth = currentDate.date();

            if (!leavesByDay[dayOfMonth]) {
              leavesByDay[dayOfMonth] = [];
            }
            leavesByDay[dayOfMonth].push(leave);

            currentDate = currentDate.add(1, "day");
          }
        }
      });

      Object.assign(leaveMap, leavesByDay);
    }

    return { eventMap: map, leaveDataMap: leaveMap };
  }, [holidaysData, leavesData, currentMonth, currentYear]);

  const calendarDaysWithContext = useMemo(() => {
    const firstDay = dayjs(`${currentYear}-${currentMonth}-01`);
    const lastDay = firstDay.endOf("month");

    const lastDate = lastDay.date();
    const firstDayOfWeek = (firstDay.day() + 6) % 7;

    const result: Array<{
      day: number;
      isCurrentMonth: boolean;
      weekIndex: number;
      dayIndex: number;
    }> = [];

    let weekIndex = 0;
    const prevMonthLastDate = firstDay.subtract(1, "day").date();

    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      result.push({
        day: prevMonthLastDate - i,
        isCurrentMonth: false,
        weekIndex,
        dayIndex: firstDayOfWeek - 1 - i,
      });
    }

    for (let day = 1; day <= lastDate; day++) {
      const dayIndex = (firstDayOfWeek + day - 1) % 7;

      if (dayIndex === 0 && day > 1) weekIndex++;

      result.push({
        day,
        isCurrentMonth: true,
        weekIndex,
        dayIndex,
      });
    }

    let nextMonthDay = 1;
    while (result.length < 42) {
      const dayIndex = result.length % 7;

      if (dayIndex === 0) weekIndex++;

      result.push({
        day: nextMonthDay++,
        isCurrentMonth: false,
        weekIndex,
        dayIndex,
      });
    }

    return result;
  }, [currentMonth, currentYear]);

  const DayCell = ({
    day,
    isCurrentMonth,
    events,
    leaveData,
  }: {
    day: number;
    isCurrentMonth: boolean;
    events?: Array<{ type: keyof typeof INDICATOR_COLORS; label: string }>;
    leaveData?: any[];
  }) => {
    const textOpacity = isCurrentMonth ? "text-white" : "text-gray-500";

    const handleCellClick = () => {
      if (!isCurrentMonth) return;

      if (leaveData && leaveData.length > 0) {
        handleOpen({
          type: "VIEW",
          data: {
            date: `${currentYear}-${String(currentMonth).padStart(
              2,
              "0"
            )}-${String(day).padStart(2, "0")}`,
            leaves: leaveData,
            day: day,
            month: currentMonth,
            year: currentYear,
          },
        });
      }
    };

    return (
      <div
        className={cn(
          "w-full aspect-square min-h-[140px] rounded-xl bg-transparent border-2 border-slate-700 flex flex-col p-2 text-center relative",
          textOpacity,
          isCurrentMonth &&
            leaveData?.length &&
            "cursor-pointer hover:bg-slate-700/20"
        )}
        onClick={handleCellClick}
      >
        <span className="text-lg font-semibold mb-2">{day}</span>
        
        {isCurrentMonth && events && events.length > 0 && (
          <div className="flex flex-col gap-1 text-left text-xs text-white overflow-hidden">
            {events.map((event, idx) => (
              <div key={idx} className="flex items-start gap-1.5">
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0 mt-0.5"
                  style={{ backgroundColor: INDICATOR_COLORS[event.type] }}
                ></span>
                <span className="leading-tight line-clamp-2">{event.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="w-full dark:bg-theme-slate-800 border dark:border-theme-gray-700 rounded-xl text-white p-6 custom-scroll overflow-x-auto">
        <div className="flex items-start justify-between bg-transparent h-[52px] rounded-md !mb-12">
          <div>
            <div className="text-xl lg:text-3xl font-bold">
              Organization Calendar
            </div>
            <div className="justify-start text-white text-lg font-normal capitalize mt-1">
              {formatDate(
                dayjs()
                  .year(currentYear)
                  .month(currentMonth - 1)
                  .toString(),
                "MMMM YYYY"
              )}
            </div>
          </div>

          <div className="flex flex-col text-white text-base font-normal">
            <span className="mb-4 text-xl font-semibold text-white">
              Indicator
            </span>

            <div className="flex items-center gap-6">
              <span className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-[#3E8BFF]"></span>
                Government Holiday
              </span>

              <span className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-[#00C897]"></span>
                Festival
              </span>

              <span className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-[#C026D3]"></span>
                Event
              </span>

              <span className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-[#6B7280]"></span>
                Weekly Holiday
              </span>

              <span className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-[#FACC15]"></span>
                Employee Leave
              </span>
            </div>
          </div>
        </div>

        {holidaysLoading ||
        holidaysFetching ||
        leavesLoading ||
        leavesFetching ? (
          <div className="grid grid-cols-7 gap-3 min-w-[1000px]">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
              <div
                key={`header-${d}`}
                className="w-full aspect-square min-h-[140px] rounded-xl bg-[#374151] border border-slate-700 flex items-center justify-center text-center animate-pulse"
              >
                <div className=" bg-slate-600 rounded"></div>
              </div>
            ))}

            {Array.from({ length: 42 }, (_, index) => (
              <div
                key={`skeleton-${index}`}
                className="w-full aspect-square min-h-[140px] rounded-xl bg-transparent border border-slate-700 flex flex-col p-2 animate-pulse"
              >
                <div className="flex justify-center mb-2">
                  <div className="w-8 h-8 bg-slate-600 rounded-full"></div>
                </div>
                <div className="flex-1 space-y-1">
                  <div className="w-full h-3 bg-slate-600 rounded"></div>
                  <div className="w-3/4 h-3 bg-slate-600 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-7 gap-3 min-w-[1000px]">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
              <div
                key={d}
                className="w-full aspect-square min-h-[140px] rounded-xl flex items-center justify-center text-center bg-[#374151] border border-slate-700 text- text-xl font-bold"
              >
                {d}
              </div>
            ))}

            {calendarDaysWithContext.map((dayInfo, index) => {
              const { day, isCurrentMonth, dayIndex } = dayInfo;

              const isFriday = dayIndex === 4;
              const dayLeaveData = isCurrentMonth
                ? leaveDataMap[day]
                : undefined;

              let events: Array<{ type: keyof typeof INDICATOR_COLORS; label: string }> = [];

              // Add weekly holiday for Friday
              if (isCurrentMonth && isFriday) {
                events.push({
                  type: "holiday",
                  label: "Weekly Holiday",
                });
              }

              // Add holidays from API
              if (isCurrentMonth && eventMap[day]) {
                events = [...events, ...eventMap[day]];
              }

              // Add leave indicator
              if (dayLeaveData && dayLeaveData.length > 0) {
                const leaveCount = dayLeaveData.length;
                events.push({
                  type: "leave",
                  label: `${leaveCount} Employee${leaveCount > 1 ? "s" : ""} On Leave`,
                });
              }

              return (
                <DayCell
                  key={index}
                  day={day}
                  isCurrentMonth={isCurrentMonth}
                  events={events.length > 0 ? events : undefined}
                  leaveData={dayLeaveData}
                />
              );
            })}
          </div>
        )}
      </div>

      <EmployeeLeaveView
        data={open.data}
        type={open.type}
        handleClose={handleClose}
        open={open.open && open.type === "VIEW" && !!open.data?.leaves?.length}
      />
    </>
  );
}
*/
